import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol.js";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol.js";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import EditorVM from "@arcgis/core/widgets/Editor/EditorViewModel.js";
import type Point from "@arcgis/core/geometry/Point";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { AppConfig } from "../../../AppConfig";
import AddIcon from "../../../assets/add.svg?react";
import AllIcon from "../../../assets/all.svg?react";
import LastUsedIcon from "../../../assets/last-used.svg?react";
import { RightPanelTabs } from "../../../constants/Panels";
import { AppContext, UserContext } from "../../../Context";
import { setCreateObjectOpen, setLoading, setRightPanelActiveTab } from "../../../store/appSlice";
import { setSectorSourceStep } from "../../../store/lighthouseSlice";
import {
    createRelatedFeatures,
    evaluateArcadeExpression,
    findAndSelectFeature,
    getMapView,
} from "../../../utils/arcgisUtils";
import FloatingWindow from "../../genericComponents/FloatingWindow/FloatingWindow";
import AllObjectTypesPanel from "./AllObjectTypesPanel";
import "./CreateObjectControl.css";
import LatestObjectTypesPanel from "./LatestObjectTypesPanel";
import type Symbol from "@arcgis/core/symbols/Symbol";

const CreateObjectControl = () => {
    const dispatch = useDispatch();
    const createToolRef = useRef(document.createElement("div"));
    const [activeLayerId, setActiveLayerId] = useState<string | null>(null);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [newFeature, setNewFeature] = useState<Graphic | null>(null);
    const [renderSymbol, setRenderSymbol] = useState<Symbol | null | undefined>(undefined);

    const createObjectVMRef = useRef<EditorVM | null>(null);
    // The preset feature for a lighthouse waiting to be placed, or null. A ref, not state:
    // it is read inside the sketch-create handler, which is registered once per symbol
    // change and would otherwise close over a stale value.
    const pendingLighthouseRef = useRef<Graphic | null>(null);
    const appContext = useContext(AppContext);
    const userContext = useContext(UserContext);
    const userName = userContext?.user.value?.fullName;

    useEffect(() => {
        const mapview = getMapView();
        if (!mapview?.map || !createObjectVMRef.current || !activeLayerId) return;

        const layer = mapview.map.findLayerById(activeLayerId);
        if (!layer) return;

        const geometryType = (layer as FeatureLayer).geometryType;
        if (geometryType === "multipatch") return;

        layer.visible = true;

        const renderer = (layer as FeatureLayer).renderer;
        if (renderer?.type === "simple") {
            const symbol = renderer.symbol;
            setRenderSymbol(symbol);
        } else if (renderer?.type === "unique-value") {
            const uniqueValueRenderer = renderer;
            if (uniqueValueRenderer.field === null) {
                evaluateArcadeExpression(uniqueValueRenderer.valueExpression ?? "", newFeature).then((value) => {
                    const valueInfo = uniqueValueRenderer.uniqueValueInfos?.find((info) => info.value === value);
                    const valueInfoSymbol = valueInfo?.symbol;
                    setRenderSymbol(valueInfoSymbol);
                });
            } else {
                const rendererFields: { key: string; value: any }[] = [];
                for (const prop in uniqueValueRenderer) {
                    if (prop.startsWith("field")) {
                        const field = uniqueValueRenderer[prop as keyof typeof uniqueValueRenderer];
                        if (field !== null && field !== undefined) {
                            rendererFields.push({ key: prop, value: field });
                        }
                    }
                }
                let value = "";
                for (let i = 0; i < rendererFields.length - 1; i++) {
                    const fieldValue = newFeature?.attributes[rendererFields[i].value];
                    if (fieldValue !== null && fieldValue !== undefined && value !== "") {
                        value += rendererFields[rendererFields.length - 1].value;
                    }
                    if (fieldValue !== null && fieldValue !== undefined) {
                        value += fieldValue.toString();
                    } else {
                        value += "0";
                    }
                }
                const valueInfo = uniqueValueRenderer.uniqueValueInfos?.find((info) => info.value === value.toString());
                const valueInfoSymbol = valueInfo?.symbol;
                setRenderSymbol(valueInfoSymbol);
            }
        } else {
            return;
        }
    }, [newFeature]);

    useEffect(() => {
        const mapview = getMapView();
        if (!mapview?.map || !createObjectVMRef.current || !activeLayerId) return;

        const layer = mapview.map.findLayerById(activeLayerId) as FeatureLayer;
        if (!layer) return;

        const geometryType = layer.geometryType;

        if (!createObjectVMRef.current.sketchViewModel) return;

        if (geometryType === "point") {
            createObjectVMRef.current.sketchViewModel.pointSymbol = renderSymbol as SimpleMarkerSymbol;
        } else if (geometryType === "polyline") {
            createObjectVMRef.current.sketchViewModel.polylineSymbol = renderSymbol as SimpleLineSymbol;
        } else if (geometryType === "polygon") {
            createObjectVMRef.current.sketchViewModel.polygonSymbol = renderSymbol as SimpleFillSymbol;
        } else {
            return;
        }

        createObjectVMRef.current.sketchViewModel.create(geometryType);

        createObjectVMRef.current?.on("sketch-create", async (event) => {
            if (event.detail.state === "complete" && event.detail.graphic) {
                dispatch(setLoading(true));
                const graphic = event.detail.graphic;

                dispatch(setCreateObjectOpen(false));

                const createObjectCurrentEntry = appContext?.createObjectCurrentEntryRef.value.current;

                const pendingLighthouse = pendingLighthouseRef.current;
                if (pendingLighthouse) {
                    // The sketch only decided where the lighthouse goes. Creating it waits for
                    // the user to choose default sectors or define them in a table, which
                    // happens in SectorSourceDialog - this component unmounts before then.
                    appContext!.pendingLighthousePlacement.value.current = {
                        feature: pendingLighthouse,
                        center: graphic.geometry as Point,
                    };

                    (createObjectVMRef.current?.sketchViewModel?.layer as GraphicsLayer)?.removeAll();
                    pendingLighthouseRef.current = null;

                    // Cleared before the dialog opens, or the spinner sits on top of it
                    dispatch(setLoading(false));
                    dispatch(setSectorSourceStep("choosing"));
                    return;
                }

                const selectedLayerConfig = AppConfig.CreateObject.flatMap((group) => group.GroupEntries).find(
                    (entry) => entry.Title === createObjectCurrentEntry
                );

                await createRelatedFeatures(graphic, layer, selectedLayerConfig, userName);

                appContext?.selectedFeature.set(graphic);

                (createObjectVMRef.current?.sketchViewModel?.layer as GraphicsLayer)?.removeAll();
                findAndSelectFeature(layer, graphic, (graphic) => {
                    appContext?.selectedFeature.set(graphic);
                });

                dispatch(setRightPanelActiveTab(RightPanelTabs.EDITOR));
                appContext!.createObjectCurrentEntryRef.value.current = null;
                dispatch(setLoading(false));
            }
        });
    }, [renderSymbol]);

    useEffect(() => {
        const mapview = getMapView();
        if (!mapview) return;

        const editorVM = new EditorVM({
            view: mapview,
        });
        createObjectVMRef.current = editorVM;

        return () => {
            if (createObjectVMRef.current) {
                createObjectVMRef.current.destroy();
                createObjectVMRef.current = null;
            }
        };
    }, []);

    const calcHeightAndWidth = () => {
        const widthAndHeight = { x: 0, y: 0 };

        widthAndHeight.x = Math.max(window.innerWidth - Math.min(460, window.innerWidth - 40), 200);
        widthAndHeight.y = 40;
        return widthAndHeight;
    };

    return editMode ? null : (
        <FloatingWindow
            className={"floating-window-create-object"}
            closeWindow={() => dispatch(setCreateObjectOpen(false))}
            toolRef={createToolRef}
            title="Opprett objekt"
            Icon={AddIcon}
            defaultPosition={calcHeightAndWidth()}
        >
            <Tabs className="h-100 flex-col" selectedTabClassName="selected">
                <TabList className="tab-list">
                    <Tab className="tab">
                        <AllIcon />
                        <span>Alle</span>
                    </Tab>
                    <Tab className="tab">
                        <LastUsedIcon />
                        <span>Sist brukt</span>
                    </Tab>
                </TabList>
                <TabPanel className="tab-panel">
                    <AllObjectTypesPanel
                        setActiveLayerId={setActiveLayerId}
                        setEditMode={setEditMode}
                        setNewFeature={setNewFeature}
                        setPendingLighthouseFeature={(feature) => (pendingLighthouseRef.current = feature)}
                    />
                </TabPanel>
                <TabPanel className="tab-panel">
                    <LatestObjectTypesPanel
                        setActiveLayerId={setActiveLayerId}
                        setEditMode={setEditMode}
                        setNewFeature={setNewFeature}
                        setPendingLighthouseFeature={(feature) => (pendingLighthouseRef.current = feature)}
                    />
                </TabPanel>
            </Tabs>
        </FloatingWindow>
    );
};

export default CreateObjectControl;
